import { useState, useRef, useEffect } from 'react';
import { useWidget } from '../WidgetProvider';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Loader2, RotateCcw } from 'lucide-react';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 30; // seconds

export function VerifyCodeStep() {
  const { state, verifyCode, sendVerificationCode, goToStep, isLoading, trackEvent, error: providerError, updateLead, checkCredits, triggerGeneration } = useWidget();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN);
  const [canResend, setCanResend] = useState(false);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) {
      setCanResend(true);
      return;
    }

    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Auto-submit when code is complete
  useEffect(() => {
    if (code.length === CODE_LENGTH) {
      handleVerify();
    }
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerify = async () => {
    if (code.length !== CODE_LENGTH) {
      setError('Please enter the full 6-digit code');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const success = await verifyCode(code);
      if (success) {
        trackEvent('code_verified');

        // Update lead with phone (verification status is set by verify/check endpoint)
        try {
          await updateLead({
            clientPhone: state.phoneNumber,
          });
        } catch (err) {
          console.error('Failed to update lead with phone:', err);
        }

        // Check if tenant has credits
        try {
          const { hasCredits } = await checkCredits();

          // If has credits, trigger generation in background
          if (hasCredits) {
            triggerGeneration();
          }
        } catch (err) {
          console.error('Failed to check credits:', err);
        }

        trackEvent('full_image_viewed');
      } else {
        setError('Invalid code. Please try again.');
        setCode('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      setCode('');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    setCanResend(false);
    setResendCooldown(RESEND_COOLDOWN);
    setCode('');
    setError(null);

    try {
      // Call the provider's sendVerificationCode to resend via Twilio
      await sendVerificationCode();
      trackEvent('code_resent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
      // Re-enable resend button if it failed
      setCanResend(true);
    }
  };

  const formatPhoneNumber = (phone: string | null): string => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-lg font-semibold">Enter verification code</h2>
        <p className="text-sm text-muted-foreground mt-1">
          We sent a 6-digit code to{' '}
          <span className="font-medium">{formatPhoneNumber(state.phoneNumber)}</span>
        </p>
      </div>

      {/* OTP Input */}
      <div className="flex justify-center">
        <InputOTP
          maxLength={CODE_LENGTH}
          value={code}
          onChange={setCode}
          disabled={isVerifying}
        >
          <InputOTPGroup>
            {Array.from({ length: CODE_LENGTH }).map((_, i) => (
              <InputOTPSlot key={i} index={i} className="w-12 h-14 text-xl" />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>

      {/* Error */}
      {(error || providerError) && (
        <p className="text-sm text-destructive text-center">{error || providerError}</p>
      )}

      {/* Verify Button */}
      <Button
        className="w-full h-12 text-base font-semibold bg-accent hover:bg-accent/90 text-accent-foreground shadow-md shadow-accent/20 transition-all hover:shadow-lg hover:shadow-accent/25 active:scale-[0.98] disabled:opacity-50 disabled:shadow-none"
        size="lg"
        onClick={handleVerify}
        disabled={code.length !== CODE_LENGTH || isVerifying || isLoading}
      >
        {isVerifying ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Verifying...
          </>
        ) : (
          'Verify & View My Design'
        )}
      </Button>

      {/* Resend */}
      <div className="text-center">
        {canResend ? (
          <Button variant="ghost" size="sm" onClick={handleResend}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Resend code
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">
            Resend code in {resendCooldown}s
          </p>
        )}
      </div>

      {/* Wrong number */}
      <p className="text-xs text-center text-muted-foreground">
        Wrong number?{' '}
        <button
          onClick={() => goToStep('phone_gate')}
          className="text-primary hover:underline"
        >
          Change phone number
        </button>
      </p>
    </div>
  );
}
